import { isTokenLetter, TOKEN_SPECS, DATE_FIELDS, TIME_FIELDS } from './pattern-token.constants';
import {
  CompiledPattern,
  FieldKind,
  FieldPart,
  PatternPart,
  PatternPartKind,
  RangeSide,
} from './date-picker.model';

type RawPart =
  | { kind: typeof PatternPartKind.Field; token: string }
  | { kind: typeof PatternPartKind.Literal; text: string };

/** Opens and closes a quoted literal in a pattern; doubled, it is an apostrophe. */
const QUOTE = "'";

const singleCache = new Map<string, CompiledPattern>();
const rangeCache = new Map<string, CompiledPattern>();

/**
 * Compiles a pattern such as `MM/dd/yyyy hh:mm aa` into an absolute character map.
 * Results are cached: the set of patterns is closed and small, but a compiled
 * pattern is read from `computed()` on every keystroke.
 */
export function compilePattern(source: string): CompiledPattern {
  const cached = singleCache.get(source);
  if (cached) return cached;

  const raw = scan(source);
  const { parts } = layout(raw, 0, 0, RangeSide.Single);
  const compiled = finalize(source, parts, false);

  singleCache.set(source, compiled);
  return compiled;
}

/**
 * Compiles `<pattern><separator><pattern>` as one flat field list, the `from`
 * fields followed by the `to` fields. Range support needs no other machinery:
 * the caret crosses the separator exactly as it crosses a `/`.
 */
export function compileRangePattern(source: string, separator: string): CompiledPattern {
  const key = `${source}\u0000${separator}`;
  const cached = rangeCache.get(key);
  if (cached) return cached;

  const raw = scan(source);
  const from = layout(raw, 0, 0, RangeSide.From);
  const separatorPart: PatternPart = {
    kind: PatternPartKind.Literal,
    text: separator,
    start: from.nextOffset,
    end: from.nextOffset + separator.length,
  };
  const to = layout(raw, separatorPart.end, from.nextFieldIndex, RangeSide.To);

  const compiled = finalize(
    `${source}${separator}${source}`,
    [...from.parts, separatorPart, ...to.parts],
    true,
  );

  rangeCache.set(key, compiled);
  return compiled;
}

/** Splits the source into token runs and literals. `'...'` quotes a literal; `''` is an apostrophe. */
function scan(source: string): RawPart[] {
  const raw: RawPart[] = [];
  let literal = '';
  let i = 0;

  const flushLiteral = () => {
    if (literal) {
      raw.push({ kind: PatternPartKind.Literal, text: literal });
      literal = '';
    }
  };

  while (i < source.length) {
    const char = source[i]!;

    if (char === QUOTE) {
      i++;
      if (source[i] === QUOTE) {
        literal += QUOTE;
        i++;
        continue;
      }
      while (i < source.length && source[i] !== QUOTE) {
        literal += source[i];
        i++;
      }
      i++;
      continue;
    }

    if (isTokenLetter(char)) {
      flushLiteral();
      let token = char;
      i++;
      while (i < source.length && source[i] === char) {
        token += char;
        i++;
      }
      raw.push({ kind: PatternPartKind.Field, token });
      continue;
    }

    literal += char;
    i++;
  }

  flushLiteral();
  return raw;
}

function layout(
  raw: readonly RawPart[],
  startOffset: number,
  startFieldIndex: number,
  side: RangeSide,
) {
  const parts: PatternPart[] = [];
  let offset = startOffset;
  let fieldIndex = startFieldIndex;

  for (const part of raw) {
    if (part.kind === PatternPartKind.Literal) {
      parts.push({
        kind: PatternPartKind.Literal,
        text: part.text,
        start: offset,
        end: offset + part.text.length,
      });
      offset += part.text.length;
      continue;
    }

    const spec = TOKEN_SPECS[part.token];
    if (!spec) {
      throw new Error(`Unsupported date pattern token: "${part.token}"`);
    }

    parts.push({
      kind: PatternPartKind.Field,
      index: fieldIndex,
      token: part.token,
      field: spec.field,
      width: spec.width,
      min: spec.min,
      max: spec.max,
      placeholder: spec.placeholder,
      start: offset,
      end: offset + spec.width,
      side,
    });
    offset += spec.width;
    fieldIndex++;
  }

  return { parts, nextOffset: offset, nextFieldIndex: fieldIndex };
}

function finalize(source: string, parts: PatternPart[], isRange: boolean): CompiledPattern {
  const fields = parts.filter((part): part is FieldPart => part.kind === PatternPartKind.Field);
  const length = parts.at(-1)?.end ?? 0;

  const fieldIndexAt: (number | null)[] = new Array(length).fill(null);
  let placeholder = '';

  for (const part of parts) {
    if (part.kind === PatternPartKind.Literal) {
      placeholder += part.text;
      continue;
    }
    placeholder += part.placeholder;
    for (let i = part.start; i < part.end; i++) {
      fieldIndexAt[i] = part.index;
    }
  }

  return {
    source,
    parts,
    fields,
    length,
    placeholder,
    fieldIndexAt,
    hasDate: fields.some((field) => DATE_FIELDS.has(field.field)),
    hasTime: fields.some((field) => TIME_FIELDS.has(field.field)),
    hour12: fields.some((field) => field.field === FieldKind.DayPeriod),
    isRange,
  };
}
